import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../services/api";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ClearOutlined,
  EditOutlined,
  LinkOutlined,
  PartitionOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useSnackbar } from "notistack";
import { getApplicationModules } from "../utils/modules";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const normalizeModuleKey = (value) => String(value || "").trim().toLowerCase();

const getModuleFeatureKey = (feature) =>
  `${feature?.application_module || ""}:${normalizeModuleKey(feature?.module_key)}`;

const createFeatureHierarchy = (items, getFeature = (item) => item) => {
  const featureMap = {};
  const moduleKeyMap = {};
  const topLevel = [];

  items.forEach((item) => {
    const feature = getFeature(item);
    if (!feature?.id) return;

    const node = { ...item, children: [], parentFeatureId: null };
    featureMap[feature.id] = node;

    if (feature.module_key) {
      moduleKeyMap[getModuleFeatureKey(feature)] = node;
    }
  });

  Object.values(featureMap).forEach((node) => {
    const feature = getFeature(node);
    const parentModuleKey = normalizeModuleKey(feature?.parent_module_key);
    const parentNode =
      (feature?.parent_id && featureMap[feature.parent_id]) ||
      (parentModuleKey && moduleKeyMap[`${feature?.application_module || ""}:${parentModuleKey}`]);

    if (parentNode && parentNode !== node) {
      node.parentFeatureId = getFeature(parentNode)?.id || null;
      parentNode.children.push(node);
    } else {
      topLevel.push(node);
    }
  });

  return { featureMap, topLevel };
};

const isActivePlanFeature = (item) => Number(item?.isDeleted ?? 0) === 0;

const PlanFeaturesManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [planFeatures, setPlanFeatures] = useState([]);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planFeatureToDelete, setPlanFeatureToDelete] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(undefined);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureSearchTerm, setFeatureSearchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [originalPlanFeatures, setOriginalPlanFeatures] = useState([]);
  const [selectedApplicationModule, setSelectedApplicationModule] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsPlanFeature, setSettingsPlanFeature] = useState(null);
  const [planFeatureSettings, setPlanFeatureSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const applicationModules = getApplicationModules();

  const fetchInitialData = useCallback(async () => {
    try {
      setInitialLoading(true);
      const [planFeaturesRes, plansRes, featuresRes] = await Promise.all([
        API.get("/plan-features"),
        API.get("/plans"),
        API.get("/features", { params: { page: 1, limit: 1000 } }),
      ]);
      setPlanFeatures((planFeaturesRes.data.data || []).filter(isActivePlanFeature));
      setPlans((plansRes.data.data || []).filter((plan) => plan.is_active));
      setFeatures((featuresRes.data.data || []).filter((feature) => feature.is_active));
    } catch (error) {
      enqueueSnackbar("Error fetching initial data", { variant: "error" });
    } finally {
      setInitialLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchPlanFeatures = useCallback(
    async (searchQuery = "") => {
      try {
        const params = searchQuery ? { search: searchQuery } : {};
        const response = await API.get("/plan-features", { params });
        setPlanFeatures((response.data.data || []).filter(isActivePlanFeature));
      } catch (error) {
        enqueueSnackbar("Error fetching plan features", { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (!initialLoading && debouncedSearchTerm !== "") {
      fetchPlanFeatures(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, fetchPlanFeatures, initialLoading]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (debouncedSearchTerm) {
        await fetchPlanFeatures(debouncedSearchTerm);
      } else {
        await fetchInitialData();
      }
    } finally {
      setRefreshing(false);
    }
  }, [debouncedSearchTerm, fetchPlanFeatures, fetchInitialData]);

  const groupedPlanFeatures = useMemo(() => {
    if (!planFeatures.length || !plans.length || !features.length) return {};

    const grouped = {};

    plans.forEach((plan) => {
      const planFeaturesForPlan = planFeatures.filter((pf) => pf.plan_id === plan.id);
      const featuresByModule = {};

      planFeaturesForPlan.forEach((pf) => {
        const feature = features.find((f) => f.id === pf.feature_id);
        if (feature) {
          const module = feature.application_module || "Other";
          if (!featuresByModule[module]) featuresByModule[module] = [];
          featuresByModule[module].push({ ...pf, featureName: feature.name, feature });
        }
      });

      if (Object.keys(featuresByModule).length > 0) {
        grouped[plan.id] = {
          plan,
          moduleFeatures: featuresByModule,
          totalFeatures: planFeaturesForPlan.length,
        };
      }
    });

    return grouped;
  }, [planFeatures, plans, features]);

  const filteredGroupedFeatures = useMemo(() => {
    if (!searchTerm) return groupedPlanFeatures;

    const filtered = {};
    const searchLower = searchTerm.toLowerCase();

    Object.entries(groupedPlanFeatures).forEach(([planId, planData]) => {
      const matchesPlan = planData.plan.name.toLowerCase().includes(searchLower);

      if (matchesPlan) {
        filtered[planId] = planData;
      } else {
        const matchingModuleFeatures = {};
        Object.entries(planData.moduleFeatures).forEach(([module, moduleItems]) => {
          const matchingFeatures = moduleItems.filter((f) =>
            f.featureName.toLowerCase().includes(searchLower)
          );
          if (matchingFeatures.length > 0) {
            matchingModuleFeatures[module] = matchingFeatures;
          }
        });

        if (Object.keys(matchingModuleFeatures).length > 0) {
          filtered[planId] = { ...planData, moduleFeatures: matchingModuleFeatures };
        }
      }
    });

    return filtered;
  }, [groupedPlanFeatures, searchTerm]);

  const buildFeatureHierarchy = useCallback(
    (moduleFilter = null) => {
      const filteredFeatures = moduleFilter
        ? features.filter((f) => f.application_module === moduleFilter)
        : features;
      return createFeatureHierarchy(filteredFeatures);
    },
    [features]
  );

  const organizeFeatureHierarchy = useCallback((featuresArray) => {
    return createFeatureHierarchy(featuresArray, (item) => item.feature).topLevel;
  }, []);

  const getAllChildIds = useCallback((feature) => {
    let ids = [];
    if (feature.children && feature.children.length > 0) {
      feature.children.forEach((child) => {
        ids.push(child.id);
        ids = [...ids, ...getAllChildIds(child)];
      });
    }
    return ids;
  }, []);

  const getAncestorIds = useCallback((feature, featureMap) => {
    const ids = [];
    let current = feature;
    const visited = new Set();

    while (current?.parentFeatureId && !visited.has(current.parentFeatureId)) {
      visited.add(current.parentFeatureId);
      ids.push(current.parentFeatureId);
      current = featureMap[current.parentFeatureId];
    }

    return ids;
  }, []);

  const handleParentChange = useCallback(
    (featureId, checked) => {
      const { featureMap } = buildFeatureHierarchy(selectedApplicationModule || null);
      const feature = featureMap[featureId];
      if (!feature) return;

      const childIds = getAllChildIds(feature);
      const allIds = [featureId, ...childIds];

      if (checked) {
        setSelectedFeatures((prev) => [...new Set([...prev, ...allIds])]);
      } else {
        setSelectedFeatures((prev) => prev.filter((id) => !allIds.includes(id)));
      }
    },
    [buildFeatureHierarchy, selectedApplicationModule, getAllChildIds]
  );

  const handleChildChange = useCallback(
    (featureId, checked) => {
      const { featureMap } = buildFeatureHierarchy(selectedApplicationModule || null);
      const feature = featureMap[featureId];
      const descendantIds = feature ? getAllChildIds(feature) : [];

      if (checked) {
        const ancestorIds = feature ? getAncestorIds(feature, featureMap) : [];
        setSelectedFeatures((prev) => [...new Set([...prev, ...ancestorIds, featureId])]);
      } else {
        setSelectedFeatures((prev) =>
          prev.filter((id) => id !== featureId && !descendantIds.includes(id))
        );
      }
    },
    [buildFeatureHierarchy, selectedApplicationModule, getAllChildIds, getAncestorIds]
  );

  const isParentIndeterminate = useCallback(
    (feature) => {
      if (!feature.children || feature.children.length === 0) return false;
      const childIds = getAllChildIds(feature);
      const selectedChildCount = selectedFeatures.filter((id) => childIds.includes(id)).length;
      return selectedChildCount > 0 && selectedChildCount < childIds.length;
    },
    [getAllChildIds, selectedFeatures]
  );

  const isParentSelected = useCallback(
    (feature) => {
      if (!feature.children || feature.children.length === 0) {
        return selectedFeatures.includes(feature.id);
      }
      const childIds = getAllChildIds(feature);
      return childIds.every((id) => selectedFeatures.includes(id)) && selectedFeatures.includes(feature.id);
    },
    [getAllChildIds, selectedFeatures]
  );

  const filteredParentFeatures = useMemo(() => {
    const { topLevel } = buildFeatureHierarchy(selectedApplicationModule || null);
    const searchTermLower = featureSearchTerm.toLowerCase();

    return topLevel.filter((parent) => {
      const matchesParent = parent.name.toLowerCase().includes(searchTermLower);
      const matchesChildren = parent.children.some((child) =>
        child.name.toLowerCase().includes(searchTermLower)
      );
      return matchesParent || matchesChildren;
    });
  }, [buildFeatureHierarchy, selectedApplicationModule, featureSearchTerm]);

  const featureGroups = useMemo(() => {
    const groups = {};
    filteredParentFeatures.forEach((parent) => {
      groups[parent.name] = {
        parent,
        children: parent.children.filter(
          (child) =>
            !featureSearchTerm ||
            child.name.toLowerCase().includes(featureSearchTerm.toLowerCase())
        ),
      };
    });
    return groups;
  }, [filteredParentFeatures, featureSearchTerm]);

  const resetForm = useCallback(() => {
    setSelectedPlan(undefined);
    setSelectedFeatures([]);
    setFeatureSearchTerm("");
    setIsEditing(false);
    setEditingId(null);
    setIsBulkEditing(false);
    setOriginalPlanFeatures([]);
    setSelectedApplicationModule("");
  }, []);

  const handleBulkEditClick = useCallback(
    (planGroup, moduleFilter = null) => {
      setSelectedPlan(planGroup.plan.id);
      setSelectedApplicationModule(moduleFilter || "");

      let currentFeatureIds = [];
      if (moduleFilter) {
        currentFeatureIds = planGroup.moduleFeatures[moduleFilter]?.map((f) => f.feature_id) || [];
      } else {
        Object.values(planGroup.moduleFeatures).forEach((moduleFeatures) => {
          currentFeatureIds.push(...moduleFeatures.map((f) => f.feature_id));
        });
      }

      const { featureMap } = buildFeatureHierarchy(moduleFilter || null);
      const expandedFeatureIds = new Set(currentFeatureIds);
      currentFeatureIds.forEach((featureId) => {
        const feature = featureMap[featureId];
        if (!feature) return;
        getAncestorIds(feature, featureMap).forEach((ancestorId) => {
          expandedFeatureIds.add(ancestorId);
        });
      });

      setSelectedFeatures([...expandedFeatureIds]);
      setOriginalPlanFeatures(
        moduleFilter
          ? planGroup.moduleFeatures[moduleFilter] || []
          : Object.values(planGroup.moduleFeatures).flat()
      );

      setIsBulkEditing(true);
      setOpen(true);
    },
    [buildFeatureHierarchy, getAncestorIds]
  );

  const handleBulkAddSubmit = async () => {
    if (!selectedPlan || selectedFeatures.length === 0) {
      enqueueSnackbar("Please select a plan and at least one feature", { variant: "error" });
      return;
    }

    try {
      if (isEditing && !isBulkEditing) {
        await API.put(`/plan-features/${editingId}`, {
          plan_id: selectedPlan,
          feature_id: selectedFeatures[0],
        });
        enqueueSnackbar("Plan feature updated successfully", { variant: "success" });
      } else if (isBulkEditing) {
        const originalFeatureIds = originalPlanFeatures.map((f) => f.feature_id);
        const featuresToAdd = selectedFeatures.filter((id) => !originalFeatureIds.includes(id));
        const featuresToRemove = originalFeatureIds.filter((id) => !selectedFeatures.includes(id));
        const promises = [];

        if (featuresToAdd.length > 0) {
          promises.push(
            API.post("/plan-features/bulk-add", {
              planId: selectedPlan,
              featureIds: featuresToAdd,
            })
          );
        }

        if (featuresToRemove.length > 0) {
          const featuresToDelete = originalPlanFeatures.filter((f) =>
            featuresToRemove.includes(f.feature_id)
          );
          featuresToDelete.forEach((feature) => {
            promises.push(API.delete(`/plan-features/${feature.id}`));
          });
        }

        if (promises.length > 0) {
          await Promise.all(promises);
          enqueueSnackbar("Plan features updated successfully", { variant: "success" });
        } else {
          enqueueSnackbar("No changes made", { variant: "info" });
        }
      } else {
        await API.post("/plan-features/bulk-add", {
          planId: selectedPlan,
          featureIds: selectedFeatures,
          applicationModule: selectedApplicationModule || undefined,
        });
        enqueueSnackbar(`Successfully added ${selectedFeatures.length} features to the plan`, {
          variant: "success",
        });
      }

      await fetchPlanFeatures(debouncedSearchTerm);
      setOpen(false);
      resetForm();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || error.message || "Error saving plan features", {
        variant: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!planFeatureToDelete) return;

    try {
      await API.delete(`/plan-features/${planFeatureToDelete.id}`);
      enqueueSnackbar("Plan feature deleted successfully", { variant: "success" });
      setPlanFeatures((prev) => prev.filter((pf) => pf.id !== planFeatureToDelete.id));
    } catch (error) {
      enqueueSnackbar("Error deleting plan feature", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setPlanFeatureToDelete(null);
    }
  };

  const closeSettingsDialog = () => {
    setSettingsOpen(false);
    setSettingsPlanFeature(null);
    setPlanFeatureSettings([]);
  };

  const openSettingsDialog = useCallback(async (planFeature) => {
    setSettingsPlanFeature(planFeature);
    setSettingsOpen(true);
    setSettingsLoading(true);
    try {
      const response = await API.get(`/plan-features/${planFeature.id}/settings`);
      setPlanFeatureSettings(response.data.data || []);
    } catch (error) {
      enqueueSnackbar("Error fetching plan feature settings", { variant: "error" });
    } finally {
      setSettingsLoading(false);
    }
  }, [enqueueSnackbar]);

  const updatePlanFeatureSetting = (settingId, changes) => {
    setPlanFeatureSettings((previous) =>
      previous.map((setting) =>
        setting.id === settingId ? { ...setting, ...changes } : setting
      )
    );
  };

  const serializePlanFeatureSettings = () =>
    planFeatureSettings.map((setting) => {
      let settingValue = setting.setting_value;
      if (setting.setting_type === "json" && typeof settingValue === "string") {
        const trimmedValue = settingValue.trim();
        settingValue = trimmedValue ? JSON.parse(trimmedValue) : null;
      }
      return {
        feature_setting_id: setting.id,
        is_enabled: setting.is_enabled,
        setting_value: settingValue,
      };
    });

  const handleSavePlanFeatureSettings = async () => {
    if (!settingsPlanFeature) return;
    setSettingsLoading(true);
    try {
      await API.put(`/plan-features/${settingsPlanFeature.id}/settings`, {
        settings: serializePlanFeatureSettings(),
      });
      enqueueSnackbar("Plan feature settings saved successfully", { variant: "success" });
      closeSettingsDialog();
    } catch (error) {
      const message =
        error instanceof SyntaxError
          ? "JSON setting values must be valid JSON"
          : error.response?.data?.message || "Error saving plan feature settings";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSettingsLoading(false);
    }
  };

  const renderSettingValueInput = (setting) => {
    if (setting.setting_type === "boolean") {
      return null;
    }

    const disabled = settingsLoading || !setting.is_enabled;

    if (setting.setting_type === "number") {
      return (
        <InputNumber
          value={typeof setting.setting_value === "number" ? setting.setting_value : null}
          onChange={(value) => updatePlanFeatureSetting(setting.id, { setting_value: value })}
          placeholder="Enter a number"
          disabled={disabled}
          style={{ width: "100%" }}
        />
      );
    }

    if (setting.setting_type === "json") {
      const value =
        typeof setting.setting_value === "string"
          ? setting.setting_value
          : setting.setting_value == null
          ? ""
          : JSON.stringify(setting.setting_value, null, 2);
      return (
        <Input.TextArea
          value={value}
          onChange={(event) => updatePlanFeatureSetting(setting.id, { setting_value: event.target.value })}
          placeholder='Enter JSON, for example: {"limit": 10}'
          autoSize={{ minRows: 2, maxRows: 6 }}
          disabled={disabled}
        />
      );
    }

    return (
      <Input
        value={setting.setting_value ?? ""}
        onChange={(event) => updatePlanFeatureSetting(setting.id, { setting_value: event.target.value })}
        placeholder="Enter a value"
        disabled={disabled}
      />
    );
  };

  const renderHierarchyRows = useCallback(
    (featuresArray, level = 0) => {
      const hierarchicalFeatures = organizeFeatureHierarchy(featuresArray);

      return hierarchicalFeatures.map((feature) => (
        <div key={feature.id} className="feature-row" style={{ paddingLeft: level * 16 }}>
          {level > 0 && <span className="feature-branch" />}
          <PartitionOutlined style={{ marginRight: 8, color: "rgba(100, 116, 139, 0.8)" }} />
          <Tag color={level === 0 ? "blue" : "geekblue"}>{feature.featureName}</Tag>
          <Tooltip title="Feature Settings">
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => openSettingsDialog(feature)}
            />
          </Tooltip>
          {feature.children.length > 0 && (
            <div style={{ width: "100%", marginTop: 8 }}>
              {renderHierarchyRows(feature.children, level + 1)}
            </div>
          )}
        </div>
      ));
    },
    [organizeFeatureHierarchy, openSettingsDialog]
  );

  const planPanels = Object.entries(filteredGroupedFeatures).map(([planId, planData]) => {
    const modules = Object.keys(planData.moduleFeatures);
    const items = modules.map((module, index) => ({
      key: module,
      label: (
        <Space size={8}>
          <AppstoreOutlined />
          <span>{module}</span>
          <Badge count={planData.moduleFeatures[module].length} color="#4b7cf3" />
        </Space>
      ),
      children: (
        <div className="plan-module-panel">
          <div className="plan-module-header">
            <Typography.Title level={4} style={{ margin: 0 }}>
              {module} Features
            </Typography.Title>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleBulkEditClick(planData, module)}>
              Edit Module
            </Button>
          </div>

          <Card size="small" variant="borderless" className="feature-list-card">
            {planData.moduleFeatures[module].length > 0 ? (
              <div className="feature-tree">{renderHierarchyRows(planData.moduleFeatures[module])}</div>
            ) : (
              <Empty description={`No ${module} features mapped to this plan`} />
            )}
          </Card>
        </div>
      ),
    }));

    return (
      <Card
        key={planId}
        className="plan-card app-page-card"
        variant="borderless"
        style={{ marginBottom: 20 }}
        title={
          <Space size={10}>
            <ApartmentOutlined />
            <span>{planData.plan.name}</span>
          </Space>
        }
        extra={
          <Space size={10}>
            <Badge count={planData.totalFeatures} color="#4b7cf3" />
            <Button size="small" icon={<EditOutlined />} onClick={() => handleBulkEditClick(planData)}>
              Edit All
            </Button>
          </Space>
        }
      >
        <Tabs items={items} defaultActiveKey={modules[0]} />
      </Card>
    );
  });

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <LinkOutlined style={{ fontSize: 56, color: "rgba(100, 116, 139, 0.45)" }} />
      <Typography.Title level={4} style={{ marginTop: 16 }}>
        {searchTerm ? "No matching plan features found" : "No plan features found"}
      </Typography.Title>
      <Typography.Text type="secondary">
        {searchTerm
          ? "Clear the search to see all plan mappings."
          : "Map the first set of features to a plan to begin."}
      </Typography.Text>
      <div style={{ marginTop: 20 }}>
        {searchTerm ? (
          <Button icon={<ClearOutlined />} onClick={() => setSearchTerm("")}>
            Clear search
          </Button>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Map plan features
          </Button>
        )}
      </div>
    </div>
  );

  const featureGroupsContent = Object.keys(featureGroups).length === 0 ? (
    <Empty description="No features found" />
  ) : (
    Object.entries(featureGroups).map(([parentName, group]) => (
      <Card key={group.parent.id} size="small" style={{ marginBottom: 12 }}>
        <div className="selector-node-row">
          <Checkbox
            checked={isParentSelected(group.parent)}
            indeterminate={isParentIndeterminate(group.parent)}
            onChange={(e) => handleParentChange(group.parent.id, e.target.checked)}
            disabled={isEditing && !isBulkEditing}
          />
          <Typography.Text style={{ marginLeft: 10, fontWeight: 600 }}>{parentName}</Typography.Text>
          <Tag color="blue" style={{ marginLeft: 10 }}>
            {group.parent.application_module}
          </Tag>
        </div>
        <div className="selector-children" style={{ marginTop: 12 }}>
          {group.children.map((child) => (
            <div key={child.id} className="selector-child-row">
              <Checkbox
                checked={selectedFeatures.includes(child.id)}
                onChange={(e) => handleChildChange(child.id, e.target.checked)}
                disabled={isEditing && !isBulkEditing}
              />
              <Typography.Text style={{ marginLeft: 10 }}>{child.name}</Typography.Text>
              <Tag color="blue" style={{ marginLeft: 10 }}>
                {child.application_module}
              </Tag>
            </div>
          ))}
        </div>
      </Card>
    ))
  );

  return (
    <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 24 } }}>
      <div className="page-toolbar">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Plan Features Management
          </Typography.Title>
        </div>
        <Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing || initialLoading}
          />
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpen(true)}
            disabled={initialLoading}
          >
            Map Plan Features
          </Button>
        </Space>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search plan features..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          style={{ width: 300 }}
          disabled={initialLoading}
        />
      </Space>

      {initialLoading ? (
        <Empty description="Loading plan features..." />
      ) : Object.keys(filteredGroupedFeatures).length === 0 ? (
        emptyState
      ) : (
        planPanels
      )}

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          resetForm();
        }}
        title={
          isEditing && !isBulkEditing
            ? "Edit Plan Feature"
            : isBulkEditing
            ? `Edit Plan Features${selectedApplicationModule ? ` (${selectedApplicationModule})` : ""}`
            : "Map Features to Plan"
        }
        width={1100}
        onOk={handleBulkAddSubmit}
        okText={isEditing && !isBulkEditing ? "Update" : "Save"}
      >
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          <Select
            value={selectedPlan}
            onChange={(value) => setSelectedPlan(value)}
            placeholder="Select Plan"
            disabled={isEditing || isBulkEditing}
            options={plans.map((plan) => ({ value: plan.id, label: plan.name }))}
            style={{ width: "100%" }}
          />

          <Select
            value={selectedApplicationModule}
            onChange={(value) => {
              setSelectedApplicationModule(value);
              setSelectedFeatures([]);
            }}
            placeholder="Application Module"
            options={applicationModules}
            style={{ width: "100%" }}
          />

          <Input
            size="small"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search features..."
            value={featureSearchTerm}
            onChange={(event) => setFeatureSearchTerm(event.target.value)}
          />

          <div className="plan-feature-selector">{featureGroupsContent}</div>
        </Space>
      </Modal>

      <Modal
        open={settingsOpen}
        onCancel={closeSettingsDialog}
        onOk={handleSavePlanFeatureSettings}
        okText="Save Settings"
        okButtonProps={{ loading: settingsLoading }}
        cancelButtonProps={{ disabled: settingsLoading }}
        title={`Feature Settings${settingsPlanFeature?.featureName ? ` — ${settingsPlanFeature.featureName}` : ""}`}
        width={600}
        destroyOnHidden
      >
        {settingsLoading && planFeatureSettings.length === 0 ? (
          <Empty description="Loading feature settings..." />
        ) : planFeatureSettings.length === 0 ? (
          <Empty description="No Feature Settings have been created for this feature yet." />
        ) : (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {planFeatureSettings.map((setting) => (
              <Card key={setting.id} size="small">
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                    <Space>
                      <Typography.Text strong>{setting.setting_key}</Typography.Text>
                      <Tag color="blue">{setting.setting_type}</Tag>
                    </Space>
                    <Space>
                      <Typography.Text type="secondary">Enabled</Typography.Text>
                      <Switch
                        checked={Boolean(setting.is_enabled)}
                        onChange={(value) => updatePlanFeatureSetting(setting.id, { is_enabled: value })}
                        disabled={settingsLoading}
                      />
                    </Space>
                  </div>
                  {renderSettingValueInput(setting)}
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </Modal>

      <Modal
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setPlanFeatureToDelete(null);
        }}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true }}
        title="Confirm delete"
      >
        <Typography.Text>
          Are you sure you want to delete this plan feature mapping?
        </Typography.Text>
      </Modal>
    </Card>
  );
};

export default PlanFeaturesManagement;
